import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import {
  ShieldCheck,
  Download,
  Search,
  Tv,
  Film,
  Fingerprint,
  ExternalLink,
  QrCode,
} from "lucide-react";
import { formatApiError } from "@/lib/format";
import { toast } from "sonner";

/**
 * Proof-of-Play Ledger
 * Signed record of every media playback delivered on the caller's screens.
 * Every row includes an HMAC-SHA256 truncation so any auditor can verify.
 */

export default function ProofOfPlay() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [q, setQ] = useState("");
  const [qrRow, setQrRow] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/proof-of-play?days=${days}&limit=500`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    if (!q) return data.rows;
    const s = q.toLowerCase();
    return data.rows.filter(
      (r) =>
        r.screen_name.toLowerCase().includes(s) ||
        r.media_name.toLowerCase().includes(s) ||
        r.id.toLowerCase().includes(s)
    );
  }, [data, q]);

  const downloadCsv = async () => {
    const token = localStorage.getItem("mirrorloops_token") || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/proof-of-play.csv?days=${days}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      toast.error("Could not export CSV");
      return;
    }
    const blob = await res.blob();
    const dl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dl;
    a.download = `proof-of-play-${days}d.csv`;
    a.click();
    URL.revokeObjectURL(dl);
  };

  const downloadQrSheet = async () => {
    const token = localStorage.getItem("mirrorloops_token") || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/proof-of-play.qr-sheet.pdf?days=${days}&limit=48`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      toast.error("Could not export QR sheet");
      return;
    }
    const blob = await res.blob();
    const dl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dl;
    a.download = `proof-of-play-qr-sheet-${days}d.pdf`;
    a.click();
    URL.revokeObjectURL(dl);
  };

  if (!data) return <div className="text-white/50 py-10">Loading ledger…</div>;

  return (
    <div className="space-y-8" data-testid="proof-of-play-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <StateBadge state="LIVE" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              Proof-of-Play ledger
            </span>
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
            Every play. Signed.
          </h1>
          <p className="text-zinc-400 mt-2 max-w-lg">
            A verifiable record of every second delivered on your screens.
            Signed by <span className="font-mono text-[#E8D6A3]">{data.signed_by}</span>{" "}
            using <span className="font-mono">{data.algorithm}</span>.
          </p>
          <PublicAuditPanel />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white/[0.02] border border-white/10 p-1">
            {[1, 7, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`pop-days-${d}`}
                className={`px-3 py-1 text-xs uppercase tracking-[0.18em] transition ${
                  days === d
                    ? "bg-[#D4B878] text-black font-bold"
                    : "text-white/50 hover:text-white"
                }`}
              >
                {d === 1 ? "24h" : `${d}d`}
              </button>
            ))}
          </div>
          <Button
            onClick={downloadCsv}
            data-testid="pop-download-csv"
            variant="outline"
            className="bg-white/[0.03] border-white/15 hover:border-white"
          >
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
          <Button
            onClick={downloadQrSheet}
            data-testid="pop-download-qr-sheet"
            variant="outline"
            className="bg-white/[0.03] border-white/15 hover:border-white"
          >
            <QrCode size={14} className="mr-2" /> QR sheet PDF
          </Button>
          <a
            href="/verify"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="pop-open-verifier"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-white/50 hover:text-[#E8D6A3] transition px-3"
          >
            Public verifier <ExternalLink size={11} />
          </a>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
        <KPI icon={<Film size={16} />} k="Plays" v={data.total.toLocaleString()} accent />
        <KPI icon={<Fingerprint size={16} />} k="Signed" v="Every row" />
        <KPI icon={<ShieldCheck size={16} />} k="Window" v={`${data.window_days} days`} />
      </div>

      <div className="border border-white/10 bg-white/[0.02]">
        <div className="p-3 border-b border-white/10 flex items-center gap-2">
          <Search size={14} className="text-white/40" />
          <Input
            placeholder="Filter by screen, media, or play id…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="pop-filter"
            className="bg-transparent border-none text-sm h-8 text-white placeholder:text-white/30"
          />
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
            {rows.length} shown
          </div>
        </div>
        <div className="overflow-x-auto" data-testid="pop-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.22em] text-white/40 border-b border-white/10">
                <th className="p-3 text-left font-normal">When</th>
                <th className="p-3 text-left font-normal">
                  <Tv size={11} className="inline mr-1" /> Screen
                </th>
                <th className="p-3 text-left font-normal">Media</th>
                <th className="p-3 text-left font-normal">
                  <Fingerprint size={11} className="inline mr-1" /> Signature
                </th>
                <th className="p-3 text-right font-normal">Audit</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-white/40">
                    No signed plays in the last {days} day{days === 1 ? "" : "s"}.
                    Once your screens run, every playback appears here.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`pop-row-${r.id}`}
                  className="border-b border-white/5 hover:bg-white/[0.02]"
                >
                  <td className="p-3 font-mono text-[11px] text-white/50">
                    {new Date(r.timestamp).toLocaleString()}
                  </td>
                  <td className="p-3 font-medium">{r.screen_name}</td>
                  <td className="p-3 text-white/80">{r.media_name}</td>
                  <td className="p-3 font-mono text-[10px] text-[#E8D6A3] break-all">
                    {r.signature}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => setQrRow(r)}
                      data-testid={`pop-qr-${r.id}`}
                      title="Show verification QR"
                      className="inline-flex items-center justify-center w-8 h-8 border border-white/10 hover:border-[#E8D6A3] hover:text-[#E8D6A3] text-white/60 transition"
                    >
                      <QrCode size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <VerifyQrDialog row={qrRow} onClose={() => setQrRow(null)} />
    </div>
  );
}

function KPI({ icon, k, v, accent }) {
  return (
    <div className={`p-5 ${accent ? "bg-gradient-to-br from-[#1a1508] to-[#0c0c0c]" : "bg-[#0A0908]"}`}>
      <div className="text-[#E8D6A3] mb-2">{icon}</div>
      <div className="text-2xl font-black">{v}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">{k}</div>
    </div>
  );
}


function VerifyQrDialog({ row, onClose }) {
  const open = !!row;
  const url = useMemo(() => {
    if (!row) return "";
    const params = new URLSearchParams({
      play_id: row.id,
      screen_id: row.screen_id,
      media_id: row.media_id,
      timestamp: row.timestamp,
      signature: row.signature,
    });
    return `${window.location.origin}/verify?${params.toString()}`;
  }, [row]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Audit link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadPng = () => {
    if (!row) return;
    const svg = document.getElementById("pop-verify-qr-svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0A0908";
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 32, 32, 448, 448);
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = png;
      a.download = `proof-${row.id.slice(0, 8)}.png`;
      a.click();
      URL.revokeObjectURL(svgUrl);
    };
    img.src = svgUrl;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#14110E] border-white/10 text-white max-w-md"
        data-testid="pop-qr-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode size={18} className="text-[#E8D6A3]" /> Verification QR
          </DialogTitle>
          <div className="text-xs text-zinc-500 mt-1">
            One scan opens the public verifier with every field pre-filled.
            Print it on a receipt, invoice, or campaign report — any auditor
            can independently confirm the play.
          </div>
        </DialogHeader>

        {row && (
          <div className="space-y-4">
            <div className="bg-white p-4 flex items-center justify-center">
              <QRCodeSVG
                id="pop-verify-qr-svg"
                value={url}
                size={240}
                bgColor="#ffffff"
                fgColor="#0A0908"
                level="M"
                includeMargin
              />
            </div>

            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-2">
              <Fingerprint size={11} />
              <span className="font-mono normal-case tracking-normal text-[#E8D6A3]">
                {row.signature}
              </span>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={copyUrl}
                data-testid="pop-qr-copy"
                variant="outline"
                className="flex-1 bg-white/[0.03] border-white/15"
              >
                Copy audit link
              </Button>
              <Button
                onClick={downloadPng}
                data-testid="pop-qr-download"
                className="flex-1 bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
              >
                <Download size={13} className="mr-2" /> Download PNG
              </Button>
            </div>

            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="pop-qr-open"
              className="block text-center text-xs text-white/50 hover:text-[#E8D6A3] pt-1"
            >
              Open in new tab →
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function PublicAuditPanel() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [handle, setHandle] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/audit/me");
        setState(r.data);
        setHandle(r.data.public_audit_handle || "");
      } catch (e) {
        // silent fail — panel just hides
      }
    })();
  }, []);

  if (!state) return null;

  const publish = async () => {
    setBusy(true);
    try {
      const r = await api.post("/audit/publish", {
        enabled: true,
        handle: handle || null,
      });
      setState({
        public_audit_enabled: r.data.enabled,
        public_audit_handle: r.data.public_audit_handle,
      });
      setHandle(r.data.public_audit_handle);
      toast.success("Public audit archive is live");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    setBusy(true);
    try {
      await api.post("/audit/publish", { enabled: false });
      setState({ ...state, public_audit_enabled: false });
      toast.success("Archive taken down");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const url = state.public_audit_handle
    ? `${window.location.origin}/audit/${state.public_audit_handle}`
    : "";

  return (
    <div
      className="mt-6 p-4 border border-white/10 bg-white/[0.02] max-w-lg"
      data-testid="pop-audit-panel"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-2 mb-2">
        <ShieldCheck size={12} className="text-emerald-400" /> Public audit archive
      </div>
      {state.public_audit_enabled ? (
        <>
          <div className="text-sm text-white/70">
            Your 12-week play totals are live at{" "}
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="pop-audit-url"
              className="text-[#E8D6A3] hover:underline font-mono text-xs"
            >
              /audit/{state.public_audit_handle}
            </a>
            .
          </div>
          <Button
            onClick={unpublish}
            disabled={busy}
            variant="ghost"
            size="sm"
            data-testid="pop-audit-unpublish"
            className="mt-2 text-red-400 hover:text-red-300 px-0"
          >
            Take down archive
          </Button>
        </>
      ) : (
        <>
          <div className="text-sm text-white/60 mb-3">
            Publish a public page showing signed weekly play totals for your
            screens. Advertisers and auditors can verify you deliver.
          </div>
          <div className="flex gap-2">
            <Input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="handle (optional)"
              data-testid="pop-audit-handle"
              className="bg-white/[0.02] border-white/10 text-sm h-9 max-w-[200px]"
            />
            <Button
              onClick={publish}
              disabled={busy}
              size="sm"
              data-testid="pop-audit-publish"
              className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
            >
              {busy ? "Publishing…" : "Publish archive"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
